import { ApiResponse } from 'apisauce';

import { UsersPayload } from '@/Modules/UserManagement/shared/types';
import debugConfig from '@/shared/config/debug.config';
import api from '@/shared/config/http.config';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import { FcmDataModel, DeleteFcmDataModel } from '@/shared/models/fcm-data.model';
import { UserAuthModel } from '@/shared/models/user-auth.model';
import { AddUserDataModel, UserModel } from '@/shared/models/user.model';
import userApiFixtureService from '@/shared/services/fixtures/user-api.service';
import { SearchUserModel } from '@/shared/services/store/global-add-user.store';

const apis = {
  registerUser: (user: AddUserDataModel): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/register', user),
  loginUser: (
    email: string,
    password: string,
    captchaToken?: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/login', { email, password, captchaToken }),
  changePassword: (
    token: string,
    password: string,
    captchaToken?: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/changePassword', { token, password, captchaToken }),
  forgotPassword: (email: string, captchaToken?: string): Promise<ApiResponse<object, ErrorInfo>> =>
    api.post('/auth/forgotPassword', { email, captchaToken }),
  validateToken: (): Promise<ApiResponse<object, ErrorInfo>> => api.get('/auth/verifyToken'),
  refreshToken: (refreshToken: string): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/refreshToken', { refreshToken }),
  getAllUsers: (query: UsersPayload): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.post('/users/search', query),
  addUser: (userModel: UserModel): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.post('/users/', userModel),
  searchUsers: ({
    phone,
    name,
    email,
    role
  }: SearchUserModel): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.post('/users/searchOne', { phone, name, email, role }),
  getUserById: (userId: string): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.get(`/users/${userId}`),
  addUnverifiedUser: (
    userData: AddUserDataModel
  ): Promise<ApiResponse<ResponseModel<AddUserDataModel>, ErrorInfo>> =>
    api.post('/users/addUser', userData),
  updateClientFcmToken: (fcmData: FcmDataModel): Promise<ApiResponse<object, ErrorInfo>> =>
    api.put('/clientToken', fcmData),
  deleteClientFcmToken: (fcmData: DeleteFcmDataModel): Promise<ApiResponse<object, ErrorInfo>> =>
    api.delete('/clientToken', {}, { params: fcmData }),
  getUserImage: (
    userId?: string
  ): Promise<ApiResponse<ResponseModel<{ url: string }>, ErrorInfo>> => {
    const endpoint = userId ? `/users/${userId}/image` : '/users/me/image';
    return api.get(endpoint);
  }
};

// TODO: Enhance the way of mocking APIs
// Annotated with the real API's type. A bare ternary infers a UNION of the
// fixture and the real service, and a union only exposes members present in
// BOTH - so any endpoint the fixture has not caught up with becomes invisible
// to callers even on the real path. The fixture deliberately implements only
// the subset the mocked flows need, hence the cast; runtime behaviour is
// unchanged and the fixture path is debug-only.
export const userApi: typeof apis = debugConfig.useFixtures
  ? (userApiFixtureService as unknown as typeof apis)
  : apis;
